import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  calculateOptimizationScore,
  scoreTier,
} from "../../../helpers/optimizationScore";
import "./WantAdOptimizationScore.scss";

const TIER_LABEL = {
  strong: "Strong",
  fair: "Getting there",
  weak: "Needs detail",
};

const TIER_MESSAGE = {
  strong: "This Want-Listing is ready to match.",
  fair: "A few tweaks will sharpen your matches.",
  weak: "Add more detail so we can match this buyer well.",
};

const GLOW_RGB = {
  strong: "58, 145, 17",
  fair: "242, 100, 25",
  weak: "235, 87, 87",
};

const ANCHOR = {
  location: "opt-anchor-location",
  budget: "opt-anchor-budget",
  basics: "opt-anchor-basics",
  details: "opt-anchor-details",
  niceToHaves: "opt-anchor-niceToHaves",
  buyer: "opt-anchor-buyer",
  polish: "opt-anchor-polish",
};

const OptRing = ({ score, size, stroke, dormant }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const shown = dormant ? 0 : Math.max(0, Math.min(100, score));
  const offset = c * (1 - shown / 100);
  const center = size / 2;
  return (
    <span
      className="opt-ring"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          className="opt-ring-track"
          cx={center}
          cy={center}
          r={r}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className="opt-ring-fill"
          cx={center}
          cy={center}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <span className="opt-ring-num">
        {dormant ? <span className="opt-ring-spark">&#10022;</span> : score}
      </span>
    </span>
  );
};

const WantAdOptimizationScore = ({ wantAdData }) => {
  const [open, setOpen] = useState(false);
  const [coach, setCoach] = useState(false);
  const { score, breakdown } = calculateOptimizationScore(wantAdData);
  const active = score > 0;
  const tier = active ? scoreTier(score) : "weak";
  const boosts = breakdown
    .filter((b) => !b.done)
    .sort((a, b) => b.max - b.earned - (a.max - a.earned));
  const doneCount = breakdown.filter((b) => b.done).length;

  const [displayScore, setDisplayScore] = useState(score);
  const [pulse, setPulse] = useState(false);
  const prevScoreRef = useRef(score);
  const prevTierRef = useRef(tier);

  useEffect(() => {
    const from = prevScoreRef.current;
    prevScoreRef.current = score;
    if (from === score) return undefined;
    let raf;
    const start = performance.now();
    const dur = 480;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayScore(Math.round(from + (score - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [score]);

  useEffect(() => {
    const rank = { weak: 0, fair: 1, strong: 2 };
    const steppedUp = rank[tier] > rank[prevTierRef.current];
    prevTierRef.current = tier;
    if (active && (steppedUp || score === 100)) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 1100);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [tier, score, active]);

  const dismissCoach = () => {
    setCoach(false);
    try {
      localStorage.setItem("blsOptScoreCoachSeen", "1");
    } catch (e) {
      setCoach(false);
    }
  };

  useEffect(() => {
    if (!active) return undefined;
    let seen = false;
    try {
      seen = localStorage.getItem("blsOptScoreCoachSeen") === "1";
    } catch (e) {
      seen = true;
    }
    if (seen) return undefined;
    setCoach(true);
    const t = setTimeout(() => {
      setCoach(false);
      try {
        localStorage.setItem("blsOptScoreCoachSeen", "1");
      } catch (e) {
        setCoach(false);
      }
    }, 8000);
    return () => clearTimeout(t);
  }, [active]);

  const goToField = (id) => {
    setOpen(false);
    const el = document.getElementById(ANCHOR[id]);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };


  const glowStyle = active
    ? {
        boxShadow: `0 8px 20px rgba(2, 41, 100, 0.16), 0 0 ${Math.round(
          4 + score * 0.32
        )}px rgba(${GLOW_RGB[tier]}, ${(0.12 + score * 0.006).toFixed(2)})`,
      }
    : undefined;

  return ReactDOM.createPortal(
    <>
      <div
        className={`opt-fab-wrap tier-${tier} ${active ? "" : "is-dormant"} ${
          open ? "is-open" : ""
        }`}
      >
        {coach && !open && (
          <div className="opt-coach" role="status">
            <div className="opt-coach-body">
              <div className="opt-coach-title">Your listing score</div>
              <div className="opt-coach-text">
                It climbs as you fill in the buyer's details. Tap to see what to
                sharpen next.
              </div>
            </div>
            <button
              type="button"
              className="opt-coach-x"
              onClick={dismissCoach}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        {open && (
          <div className="opt-fab-scrim" onClick={() => setOpen(false)} />
        )}
        {open && (
          <div className="opt-fab-panel">
            <div className="opt-fab-panel-head">
              <OptRing
                score={displayScore}
                size={40}
                stroke={5}
                dormant={!active}
              />
              <div className="opt-fab-cap">
                <div className="opt-fab-eyebrow">Optimization</div>
                {active ? (
                  <>
                    <div className="opt-fab-tier">{TIER_LABEL[tier]}</div>
                    <p className="opt-fab-msg">{TIER_MESSAGE[tier]}</p>
                  </>
                ) : (
                  <>
                    <div className="opt-fab-tier">Let's build a strong one</div>
                    <p className="opt-fab-msg">
                      Start filling in the buyer's details - your score climbs
                      with every field you add.
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="opt-fab-checklabel">
              <span>{active ? "Next to optimize" : "What we score"}</span>
              <span className="opt-fab-count">
                {doneCount} of {breakdown.length} done
              </span>
            </div>

            {boosts.length === 0 ? (
              <div className="opt-fab-alldone">
                <span className="opt-fab-check" aria-hidden="true">
                  <svg viewBox="0 0 12 12" width="10" height="10">
                    <path
                      d="M2.5 6.2 L5 8.6 L9.5 3.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                Every area is filled in - this listing is fully optimized.
              </div>
            ) : (
              <>
                <ul className="opt-fab-moves">
                  {boosts.slice(0, 3).map((b, i) => (
                    <li key={b.id}>
                      <button type="button" onClick={() => goToField(b.id)}>
                        <span className="opt-fab-move-text">
                          {active ? b.hint : b.label}
                        </span>
                        {active && i === 0 && (
                          <span className="opt-fab-move-impact">
                            High impact
                          </span>
                        )}
                        <span className="opt-fab-move-arrow" aria-hidden="true">
                          &#8594;
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                {boosts.length > 3 && (
                  <div className="opt-fab-more">
                    +{boosts.length - 3} more to reach 100
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <button
          type="button"
          className={`opt-fab ${pulse ? "is-pulsing" : ""}`}
          style={glowStyle}
          onClick={() => {
            dismissCoach();
            setOpen((o) => !o);
          }}
          aria-expanded={open}
          aria-label={
            active ? `Optimization score ${score} of 100` : "Optimization score"
          }
        >
          <OptRing score={displayScore} size={48} stroke={5} dormant={!active} />
        </button>
      </div>
    </>,
    document.body
  );
};

export default WantAdOptimizationScore;
